<template>
	<div class="video-controls" :class="{ active: customControlsSupported, fullscreen }" data-test-id="video-transport-controls" @click.stop>
		<div class="video-slider">
			<v-slider
				id="seek-slider"
				:model-value="position"
				aria-label="Seek video"
				color="white"
				track-color="#FFFFFF52"
				thumb-color="white"
				hide-details="auto"
				:min="0"
				:max="maxPosition"
				:class="{ scrubbing }"
				@touchstart.stop.prevent
				@touchend.stop.prevent
				@click.stop.prevent="emit('seek-click', $event)"
				@start="emit('scrub-start')"
				@end="emit('scrub-end')"
				@update:model-value="emit('seek', $event)" />
		</div>
		<div class="video-controls__actions d-flex flex-nowrap align-center ga-1">
			<v-btn
				data-test-id="video-controls-play"
				:aria-label="playing ? 'Pause video' : 'Play video'"
				:icon="playing ? 'icon-pause' : 'icon-play-solid'"
				:data-state="playing ? 'pause' : 'play'"
				variant="plain"
				density="comfortable"
				@click="emit('play-pause'); emit('track', 'video-controls-play-button', 'video-controls-play')" />
			<div data-test-id="video-controls-progress" class="progress" style="white-space: nowrap">
				<span class="current-time">{{ currentTimeLabel }}</span> / <span class="total-time">{{ durationLabel }}</span>
			</div>
			<v-spacer />
			<v-btn
				id="mute"
				data-test-id="video-controls-mute"
				:aria-label="muted ? 'Unmute video' : 'Mute video'"
				:data-state="muted ? 'unmute' : 'mute'"
				variant="plain"
				icon
				density="comfortable"
				@click="emit('mute'); emit('track', 'video-controls-mute-button', 'video-controls-mute')">
				<v-icon>{{ muted ? "icon-volume-slash-solid" : "icon-volume-solid" }}</v-icon>
			</v-btn>
			<slot name="before-fullscreen" />
			<v-btn
				id="fullscreen"
				data-test-id="video-controls-fullscreen"
				:aria-label="fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
				variant="plain"
				density="comfortable"
				icon
				@click="emit('fullscreen-toggle'); emit('track', 'video-controls-fullscreen-button', 'video-controls-fullscreen')">
				<v-icon>{{ fullscreen ? "icon-fullscreen-exit" : "icon-fullscreen" }}</v-icon>
			</v-btn>
			<slot name="after-fullscreen" />
		</div>
	</div>
</template>

<script setup lang="ts">
	defineProps<{
		playing: boolean;
		muted: boolean;
		fullscreen: boolean;
		scrubbing?: boolean;
		customControlsSupported?: boolean;
		position: number;
		maxPosition: number;
		currentTimeLabel: string;
		durationLabel: string;
	}>();
	const emit = defineEmits<{
		"play-pause": [];
		mute: [];
		"fullscreen-toggle": [];
		seek: [position: number];
		"seek-click": [event: MouseEvent];
		"scrub-start": [];
		"scrub-end": [];
		track: [name: string, testId: string];
	}>();
</script>

<style scoped lang="scss">
	.video-controls {
		width: 100%;
		color: #fff;
		background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 75%, rgba(0, 0, 0, 0.5) 90%, #000 100%);
		padding: 0 8px 8px;
	}

	.video-controls.active {
		display: flex;
		flex-direction: column;
	}

	.video-controls__actions {
		min-width: 0;
	}

	.video-slider :deep(.v-slider--horizontal) {
		margin-left: 18px;
		margin-right: 18px;
	}

	.video-slider :deep(.v-slider__thumb-container),
	.video-slider :deep(.v-slider__track-background),
	.video-slider :deep(.v-slider__track-fill) {
		transition: 0.1s linear;
	}

	.video-slider :deep(.scrubbing .v-slider__thumb-container),
	.video-slider :deep(.scrubbing .v-slider__track-background),
	.video-slider :deep(.scrubbing .v-slider__track-fill) {
		transition: 0s;
	}

	.video-slider :deep(.v-slider__track-container) {
		height: 6px;
		border-radius: 5px;
	}

	.video-slider :deep(.v-slider__thumb) {
		width: 18px;
		height: 18px;
		left: -9px;
	}

	.video-slider :deep(.v-slider__thumb::before) {
		width: 32px;
		height: 32px;
		transform: scale(0);
		left: -7px;
		top: -7px;
	}

	.video-slider :deep(.v-slider__thumb-container:hover .v-slider__thumb::before) {
		transform: scale(1);
	}

	.video-slider :deep(.v-slider__track-background) {
		border-top-right-radius: 5px;
		border-bottom-right-radius: 5px;
	}

	.video-slider :deep(.v-slider__track-fill) {
		border-top-left-radius: 5px;
		border-bottom-left-radius: 5px;
	}
</style>
